
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { UserRole, hasAdminAccess, hasOrgAdminAccess, hasSuperAdminAccess } from '@/context/auth/types';
import { Skeleton } from './ui/skeleton';

interface RouteGuardProps {
  requiredRole?: UserRole | string;
  allowedRoles?: (UserRole | string)[];
  children?: React.ReactNode;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({ 
  requiredRole = UserRole.USER,
  allowedRoles,
  children 
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-screen space-y-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-32 w-3/4 max-w-md" />
        <div className="flex flex-col items-center space-y-2">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
    );
  }
  
  // Redirect to auth if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Get effective role (JWT role has priority)
  const effectiveRole = user.jwt_role || user.role;
  
  // Handle onboarding redirects first
  if (effectiveRole === UserRole.NEW_SIGN_UP || effectiveRole === UserRole.IN_PROGRESS) {
    // If user is in onboarding and trying to access non-onboarding routes, redirect to onboarding
    if (requiredRole !== UserRole.NEW_SIGN_UP && !allowedRoles?.includes(effectiveRole)) {
      return <Navigate to="/onboarding" replace />;
    }
  } else {
    // If user has completed onboarding and is trying to access onboarding route, redirect to dashboard
    if (requiredRole === UserRole.NEW_SIGN_UP || allowedRoles?.includes(UserRole.NEW_SIGN_UP)) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  // Check if user has required role
  let hasAccess = false;
  
  // If allowedRoles is specified, check if user role is in the allowed list
  if (allowedRoles && allowedRoles.length > 0) {
    hasAccess = allowedRoles.includes(effectiveRole);
  } else {
    // Standard role checking
    switch (requiredRole) {
      case UserRole.SUPER_ADMIN:
        hasAccess = hasSuperAdminAccess(effectiveRole);
        break;
      case UserRole.ORG_ADMIN:
        hasAccess = hasOrgAdminAccess(effectiveRole);
        break;
      case UserRole.REST_ADMIN:
        hasAccess = hasAdminAccess(effectiveRole);
        break;
      case UserRole.NEW_SIGN_UP:
      case UserRole.IN_PROGRESS:
        hasAccess = effectiveRole === requiredRole;
        break;
      case UserRole.USER:
      default:
        // All authenticated users with standard roles have at least 'user' role
        hasAccess = ![UserRole.NEW_SIGN_UP, UserRole.IN_PROGRESS].includes(effectiveRole as UserRole);
        break;
    }
  }
  
  // Redirect based on role if user doesn't have access
  if (!hasAccess) {
    if (effectiveRole === UserRole.NEW_SIGN_UP || effectiveRole === UserRole.IN_PROGRESS) {
      return <Navigate to="/onboarding" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  // Render children or outlet
  return children ? <>{children}</> : <Outlet />;
};
